/**
 * Admin Ticketing API Contracts
 *
 * Admin-specific wire formats for:
 *   - Product tickets (ticket types) CRUD  — ticketing-service AdminProductTicketsController
 *   - Bundles CRUD                          — ticketing-service AdminBundlesController
 *   - Tickets list/detail/update            — ticketing-service AdminTicketsController
 *
 * Consumer: apps/admin  (imports from '@autopia/api-contracts')
 * Producer: ticketing-service admin module
 *
 * Re-exports request schemas from the public ticketing module where those are
 * the same (CreateBundle, UpdateBundle). Adds admin-only response schemas with
 * fields such as isActive, createdAt/updatedAt, _counts that the public API
 * does not include.
 */

import { z } from 'zod';

import { BooleanFromString } from '../common';
import {
  BundlePricingStrategySchema,
  TicketCategorySchema,
  TicketStatusSchema,
  BundleItemKeySchema,
  CreateBundleBaseSchema,
  CreateBundleItemInputSchema,
  CreateBundleSchema,
  UpdateBundleSchema,
  GetBundlesQuerySchema,
  refineBundleItemStructure,
} from '../ticketing';

// ============================================================================
// Product Tickets (Ticket Types) — Admin CRUD
// ============================================================================

export const CreateAdminProductTicketSchema = z.object({
  code: z.string().min(1).max(50).regex(/^[A-Z0-9_-]+$/),
  name: z.string().min(1).max(100),
  nameId: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  descriptionId: z.string().max(500).optional(),
  category: TicketCategorySchema,
  basePriceIdr: z.number().int().min(0),
  childPricePercentage: z.number().int().min(0).max(100).optional(),
  seniorPricePercentage: z.number().int().min(0).max(100).optional(),
  minAge: z.number().int().min(0).optional(),
  maxAge: z.number().int().min(0).optional(),
  validityDays: z.number().int().min(1).optional(),
  maxReentryCount: z.number().int().min(0).optional(),
  includesAttractions: z.boolean().optional(),
  attractionCredits: z.number().int().min(0).optional(),
  isBlackoutExempt: z.boolean().optional(),
  requiresPhoto: z.boolean().optional(),
  requiresIdVerification: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
  validFrom: z.string().optional(),
  validUntil: z.string().optional(),
  standalonePurchasable: z.boolean().optional(),
  imageUrl: z.string().max(500).optional(),
  attractionId: z.string().uuid().nullable().optional(),
  pricingScheduleId: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type CreateAdminProductTicketInput = z.infer<typeof CreateAdminProductTicketSchema>;

export const UpdateAdminProductTicketSchema = CreateAdminProductTicketSchema.partial().omit({
  code: true,
});

export type UpdateAdminProductTicketInput = z.infer<typeof UpdateAdminProductTicketSchema>;

export const AdminProductTicketsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(25),
  sortBy: z
    .enum(['createdAt', 'updatedAt', 'name', 'code', 'basePriceIdr', 'sortOrder', 'category'])
    .default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  category: TicketCategorySchema.optional(),
  isActive: BooleanFromString.optional(),
  search: z.string().optional(),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  attractionId: z.string().uuid().optional(),
});

export type AdminProductTicketsQuery = z.infer<typeof AdminProductTicketsQuerySchema>;

/** List DTO — returned by GET /admin/product-tickets */
export const AdminProductTicketSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  nameId: z.string(),
  category: TicketCategorySchema,
  basePriceIdr: z.number(),
  childPricePercentage: z.number(),
  seniorPricePercentage: z.number(),
  maxReentryCount: z.number(),
  validityDays: z.number(),
  attractionCredits: z.number(),
  includesAttractions: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number(),
  standalonePurchasable: z.boolean(),
  imageUrl: z.string().nullable(),
  attractionId: z.string().nullable(),
  attractionName: z.string().nullable(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  pricingScheduleId: z.string().nullable(),
  pricingScheduleName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Detail fields optionally present in the list response
  description: z.string().nullable().optional(),
  descriptionId: z.string().nullable().optional(),
  minAge: z.number().nullable().optional(),
  maxAge: z.number().nullable().optional(),
  isBlackoutExempt: z.boolean().optional(),
  requiresPhoto: z.boolean().optional(),
  requiresIdVerification: z.boolean().optional(),
});

export type AdminProductTicket = z.infer<typeof AdminProductTicketSchema>;

/** Detail DTO — returned by GET /admin/product-tickets/:id */
export const AdminProductTicketDetailSchema = AdminProductTicketSchema.extend({
  description: z.string().nullable(),
  descriptionId: z.string().nullable(),
  minAge: z.number().nullable(),
  maxAge: z.number().nullable(),
  isBlackoutExempt: z.boolean(),
  requiresPhoto: z.boolean(),
  requiresIdVerification: z.boolean(),
  metadata: z.record(z.unknown()).nullable().optional(),
  _counts: z.object({
    tickets: z.number(),
    orderItems: z.number(),
    dailyInventory: z.number(),
  }),
  attractionDetail: z
    .object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      category: z.string(),
    })
    .nullable(),
});

export type AdminProductTicketDetail = z.infer<typeof AdminProductTicketDetailSchema>;

export const AdminProductTicketsResponseSchema = z.object({
  productTickets: z.array(AdminProductTicketSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});

export type AdminProductTicketsResponse = z.infer<typeof AdminProductTicketsResponseSchema>;

// ============================================================================
// Bundles — Admin CRUD
// Re-use public request schemas; add admin response schema with extra fields.
// ============================================================================

// Aliases for admin consumers — the underlying schemas are already exported
// from the root index via the ticketing module; only the alias names are new.
export {
  CreateBundleBaseSchema as CreateAdminBundleBaseSchema,
  CreateBundleSchema as CreateAdminBundleSchema,
  GetBundlesQuerySchema as AdminBundlesQuerySchema,
};

export type {
  CreateBundleInput as CreateAdminBundleInput,
  GetBundlesQuery as AdminBundlesQuery,
  BundlePricingStrategyType as BundlePricingStrategy,
} from '../ticketing';

// Admin bundle update includes optional items replacement.
//
// The item shape is CreateBundleItemInputSchema rather than a re-declared inline
// object. The inline copy had already drifted from the POST contract — it was
// missing both `.min(2)` on the array and `.max(10)` on quantity, so a PATCH
// could smuggle in compositions that a POST rejects.
export const UpdateAdminBundleSchema = UpdateBundleSchema.extend({
  items: z.array(CreateBundleItemInputSchema).min(2).optional(),
}).superRefine((data, ctx) => {
  if (data.pricingStrategy === 'FIXED' && data.bundlePriceIdr === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bundlePriceIdr'], message: 'bundlePriceIdr is required for FIXED pricing strategy' });
  }
  if (data.pricingStrategy === 'DISCOUNT_ON_SUM' && !data.discountPercentage) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['discountPercentage'], message: 'discountPercentage is required for DISCOUNT_ON_SUM pricing strategy' });
  }
  if (data.items) refineBundleItemStructure(data.items, ctx);
});

export type UpdateAdminBundleInput = z.infer<typeof UpdateAdminBundleSchema>;

/**
 * Admin bundle item — the admin endpoint returns basePriceIdr (the ticket type's
 * raw base price), distinct from the public BundleItemSchema which carries
 * unitPriceIdr (the allocated price per unit after bundle pricing).
 */
export const AdminBundleItemSchema = z.object({
  id: z.string().uuid(),
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  ticketTypeNameId: z.string(),
  category: TicketCategorySchema,
  itemKey: BundleItemKeySchema,
  parentItemKey: BundleItemKeySchema.nullable(),
  /** PER ENTRY UNIT on attraction lines — see BundleItemSchema.quantity. */
  quantity: z.number().int().positive(),
  effectiveQuantity: z.number().int().positive(),
  basePriceIdr: z.number().int().nonnegative(),
});

export type AdminBundleItem = z.infer<typeof AdminBundleItemSchema>;

/** Admin bundle DTO — returned by admin bundle CRUD endpoints */
export const AdminBundleSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  nameId: z.string(),
  description: z.string().nullable(),
  descriptionId: z.string().nullable(),
  bundlePriceIdr: z.number(),
  pricingStrategy: BundlePricingStrategySchema,
  discountPercentage: z.number().int().nullable(),
  maxQuantityPerOrder: z.number().int(),
  thumbnailUrl: z.string().nullable(),
  sortOrder: z.number().int(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  items: z.array(AdminBundleItemSchema),
  sumOfPartsIdr: z.number(),
  savingsIdr: z.number(),
  savingsPercentage: z.number(),
  isActive: z.boolean(),
  metadata: z.record(z.unknown()).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _counts: z.object({ orderBundles: z.number().int() }),
});

export type AdminBundle = z.infer<typeof AdminBundleSchema>;

export const AdminBundlesResponseSchema = z.object({
  bundles: z.array(AdminBundleSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});

export type AdminBundlesResponse = z.infer<typeof AdminBundlesResponseSchema>;

// ============================================================================
// Tickets — Admin list/detail/update
// ============================================================================

export const AdminTicketsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(25),
  sortBy: z
    .enum(['createdAt', 'visitDate', 'ticketNumber', 'status'])
    .default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  status: z.string().optional(),
  search: z.string().optional(),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  category: TicketCategorySchema.optional(),
});

export type AdminTicketsQuery = z.infer<typeof AdminTicketsQuerySchema>;

/** List item DTO — returned by GET /admin/tickets */
export const AdminTicketSchema = z.object({
  id: z.string(),
  ticketNumber: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  category: TicketCategorySchema,
  attractionName: z.string().nullable(),
  status: TicketStatusSchema,
  qrCodeToken: z.string(),
  visitorName: z.string(),
  visitorEmail: z.string(),
  orderNumber: z.string(),
  orderId: z.string(),
  visitDate: z.string(),
  validFrom: z.string(),
  validUntil: z.string(),
  firstUsedAt: z.string().optional(),
  createdAt: z.string(),
});

export type AdminTicket = z.infer<typeof AdminTicketSchema>;

/** Full detail DTO — returned by GET /admin/tickets/:id */
export const AdminTicketDetailSchema = AdminTicketSchema.extend({
  ticketTypeId: z.string(),
  orderItemId: z.string().nullable(),
  customerId: z.string(),
  visitorPhone: z.string(),
  currentLocation: z.string(),
  entryCount: z.number(),
  maxReentryCount: z.number(),
  lastEntryAt: z.string().optional(),
  lastExitAt: z.string().optional(),
  isTransferred: z.boolean().optional(),
  transferredAt: z.string().optional(),
  updatedAt: z.string(),
  wristband: z
    .object({
      id: z.string(),
      wristbandId: z.string(),
      status: z.string(),
      totalScanCount: z.number(),
      issuedAt: z.string(),
      expiresAt: z.string(),
    })
    .nullable(),
  ticketType: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    category: TicketCategorySchema,
    basePriceIdr: z.number(),
    validityDays: z.number(),
    includesAttractions: z.boolean(),
    attractionCredits: z.number(),
  }),
  order: z.object({
    id: z.string(),
    orderNumber: z.string(),
    status: z.string(),
    totalIdr: z.number(),
    paymentMethod: z.string(),
    visitDate: z.string(),
    createdAt: z.string(),
  }),
  parentTicket: z
    .object({
      id: z.string(),
      ticketNumber: z.string(),
      ticketTypeName: z.string(),
      status: TicketStatusSchema,
      category: TicketCategorySchema,
    })
    .nullable(),
  childTickets: z.array(
    z.object({
      id: z.string(),
      ticketNumber: z.string(),
      ticketTypeName: z.string(),
      attractionName: z.string().nullable(),
      status: TicketStatusSchema,
    }),
  ),
});

export type AdminTicketDetail = z.infer<typeof AdminTicketDetailSchema>;

export const AdminTicketsResponseSchema = z.object({
  tickets: z.array(AdminTicketSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});

export type AdminTicketsResponse = z.infer<typeof AdminTicketsResponseSchema>;

export const UpdateAdminTicketSchema = z.object({
  status: TicketStatusSchema.or(z.literal('VOIDED')).optional(),
  visitorName: z.string().min(1).max(100).optional(),
  visitorEmail: z.string().min(1).email().max(255).optional(),
  visitorPhone: z.string().max(20).optional(),
  visitDate: z.string().min(1).optional(),
});

export type UpdateAdminTicketInput = z.infer<typeof UpdateAdminTicketSchema>;

// ============================================================================
// Physical Inventory Transactions — Admin
// ============================================================================

export const PhysicalTransactionTypeSchema = z.enum([
  'RECEIPT',
  'ISSUE',
  'ADJUSTMENT',
  'RETURN',
  'WRITE_OFF',
]);
export type PhysicalTransactionType = z.infer<typeof PhysicalTransactionTypeSchema>;

export const RecordPhysicalTransactionSchema = z.object({
  type: PhysicalTransactionTypeSchema,
  quantity: z.number().int().min(1),
  reference: z.string().max(50).optional(),
  notes: z.string().max(500).optional(),
});
export type RecordPhysicalTransactionInput = z.infer<typeof RecordPhysicalTransactionSchema>;

// ============================================================================
// Wristband admin operations — ticketing-service
// ============================================================================
//
// Distinct from `AddToBlacklistSchema` in `../validation`, which serves
// validation-service and keys on `wristbandCode`. These two key on
// `wristbandId`, matching `AdminWristbandsService.addToBlacklist(wristbandId,
// reason, blacklistedBy)` and `.bulkVoid(wristbandIds, reason)`. Two schemas
// rather than one because the two services genuinely identify a wristband by
// different columns — collapsing them would make one caller send a field the
// other ignores.

export const AdminBlacklistWristbandSchema = z.object({
  // 8, not an arbitrary cap: `WristbandBlacklist.wristbandId` is
  // `@db.VarChar(8)`. A looser bound here would let a 14-character id pass
  // validation and fail in Postgres instead — a 500 for input the schema
  // claimed was valid, which is the exact failure this schema exists to stop.
  wristbandId: z.string().min(1).max(8),
  reason: z.string().min(1),   // column is `@db.Text` — no length limit to mirror
});
export type AdminBlacklistWristbandInput = z.infer<typeof AdminBlacklistWristbandSchema>;

export const AdminBulkVoidWristbandsSchema = z.object({
  // At least one id: the handler loops over this and reports voided/failed
  // counts, so an empty array would report "0 voided" as though it had worked.
  wristbandIds: z.array(z.string().min(1).max(8)).min(1),
  reason: z.string().min(1),
});
export type AdminBulkVoidWristbandsInput = z.infer<typeof AdminBulkVoidWristbandsSchema>;
